import * as React from 'react';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { TDropdownType } from './interfaces';
import { trackDropdownLinkClick } from './tracking';

export interface IDropdownMenuLink {
  url: string;
  text: JSX.Element | string;
  target?: string;
  analyticsEvent?: string;
  analyticsLabel?: string;
  isCountrysidePromoLink?: boolean;
  isDomrfLink?: boolean;
}

export interface IDropdownMenuProps {
  hasShadow?: boolean;
  links: IDropdownMenuLink[];
  name: TDropdownType;
}

export interface IDropdownMenuState {
  dropdownOver?: boolean;
  initialized?: boolean;
  mouseInTriangle?: boolean;
  parentOver?: boolean;
}

interface ICoords {
  x: number;
  y: number;
}

interface ITriangle {
  top: ICoords;
  left: ICoords;
  right: ICoords;
}

const styles = require('./dropdown_menu.css');

// eslint-disable-next-line import/no-default-export
export default class DropdownMenu extends React.Component<IDropdownMenuProps, IDropdownMenuState> {
  private DELAY = 300;
  private containerElement: HTMLDivElement | null;
  private parentElement: HTMLDivElement | null;
  private triangle: ITriangle = {} as ITriangle;

  public constructor(props: IDropdownMenuProps) {
    super(props);

    this.state = {
      parentOver: false,
    };
  }

  public componentDidMount() {
    const { name } = this.props;
    this.parentElement = document.querySelector(`[data-fh-menu-dropdown-${name}] > a`);
    this.setState({ initialized: true });
    if (!this.parentElement) {
      return;
    }

    this.parentElement.addEventListener('mouseenter', (event: MouseEvent) => {
      this.setState(
        {
          parentOver: true,
        },
        () => {
          this.createTriangle();
          document.addEventListener('mousemove', this.mouseMoveDocument);
        },
      );
    });

    this.parentElement.addEventListener('mouseout', (event: MouseEvent) => {
      this.setState(
        {
          parentOver: false,
        },
        () => {
          document.removeEventListener('mousemove', this.mouseMoveDocument);
          setTimeout(() => !this.state.parentOver && this.setState({ mouseInTriangle: false }), this.DELAY);
        },
      );
    });
  }

  public render() {
    const { links } = this.props;
    if (!Array.isArray(links) || !links.length) {
      return <div />;
    }

    return (
      <div
        {...mergeStyles(
          styles['container'],
          this.state.initialized && styles['container--initialized'],
          this.props.hasShadow && styles['container--has_shadow'],
          styles['container--scale'],
          (this.state.parentOver || this.state.mouseInTriangle || this.state.dropdownOver) &&
            styles['container--scale_over'],
          'c-header-bottom-menu-item-dropdown',
        )}
        ref={el => (this.containerElement = el)}
        onMouseEnter={this.dropdownMouseEnter}
        onMouseLeave={this.dropdownMouseOut}
      >
        <div className="c-header-container">
          <div className="c-header-container-row">
            <div className="c-header-col c-header-left-container-skip">
              <div className={styles['list']}>{this.renderLinks()}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderLinks() {
    return this.props.links.map(
      ({ analyticsEvent, analyticsLabel, target, text, url, isCountrysidePromoLink, isDomrfLink }, index) => (
        <div key={`link-${index}`} className={styles['item']}>
          <a
            href={url}
            {...mergeStyles(
              isCountrysidePromoLink ? styles['promo-link'] : styles['link'],
              isDomrfLink && styles['domrf-promo'],
            )}
            onClick={event =>
              this.onLinkClick({
                event,
                analyticsEvent,
                analyticsLabel,
                target,
                url,
              })
            }
            target={target || '_self'}
          >
            {text}
          </a>
        </div>
      ),
    );
  }

  private dropdownMouseEnter = (e: React.MouseEvent<HTMLDivElement>) =>
    this.setState({
      dropdownOver: true,
    });

  private dropdownMouseOut = (e: React.MouseEvent<HTMLDivElement>) =>
    this.setState({
      dropdownOver: false,
    });

  private mouseMoveDocument = (e: MouseEvent) =>
    this.setState({
      mouseInTriangle: this.checkMouseInTriangle({ x: e.pageX, y: e.pageY }),
    });

  private createTriangle = () => {
    if (!this.containerElement || !this.parentElement) {
      return;
    }

    const containerRect = this.containerElement.getBoundingClientRect();
    const parentRect = this.parentElement.getBoundingClientRect();

    this.triangle.top = {
      x: parentRect.left + document.body.scrollLeft + parentRect.width / 2,
      y: parentRect.top + document.body.scrollTop + parentRect.height / 2,
    };

    this.triangle.left = {
      x: containerRect.left + document.body.scrollLeft,
      y: containerRect.top + document.body.scrollTop,
    };

    this.triangle.right = {
      x: this.triangle.top.x + containerRect.width,
      y: this.triangle.top.y,
    };
  };

  private checkMouseInTriangle = (coords: ICoords) => {
    const { left, top, right } = this.triangle;

    const a = (left.x - coords.x) * (top.y - left.y) - (top.x - left.x) * (left.y - coords.y);
    const b = (top.x - coords.x) * (right.y - top.y) - (right.x - top.x) * (top.y - coords.y);
    const c = (right.x - coords.x) * (left.y - right.y) - (left.x - right.x) * (right.y - coords.y);

    return (a >= 0 && b >= 0 && c >= 0) || (a <= 0 && b <= 0 && c <= 0);
  };

  private onLinkClick = ({
    event,
    analyticsEvent,
    analyticsLabel,
    url,
    target,
  }: {
    event: React.MouseEvent<HTMLElement>;
    url: string;
    target?: string;
    analyticsEvent?: string;
    analyticsLabel?: string;
  }) => {
    event.preventDefault();
    if (analyticsLabel) {
      trackDropdownLinkClick(analyticsLabel, analyticsEvent);
    }
    // eslint-disable-next-line no-unused-expressions
    target === '_blank' ? window.open(url) : location.assign(url);
  };
}
